import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Copy, Check, AlertTriangle, PackageX, Truck } from 'lucide-react';
import { supabase } from './supabase';
import { TableCard, Table, THead, Th, Tr, Td } from './ui/Table';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { OrderPeek, orderDisplayId } from './OrderPeek';
import { CABIFY_STATE_ES, CABIFY_DEAD } from './CabifyBatch';
import { logActivity } from './logger';

// "Demorados" (03/11/2026): envíos Cabify despachados hace más de 3 días que NO están entregados,
// separados en dos grupos con distinta acción:
//   · Cabify YA lo retiró (hay eventos de traslado/centro/camino) → el paquete lo tiene Cabify: RECLAMAR.
//   · Cabify NUNCA lo retiró del local → o sigue en el local esperando, o salió por otro lado y la
//     etiqueta quedó colgada: CANCELAR la etiqueta para que no la cobren.
// Ventana: despachados en los últimos 30 días (lo más viejo son etiquetas abandonadas).
const LATE_DAYS = 3;
const PICKED = ['pickingup', 'onroutetofinalhub', 'receivedonfinalhub', 'readytodispatch', 'intransit', 'onroutetodelivery', 'delivering', 'undelivered', 'onroutetoreturn', 'returning', 'incident'];

interface Late {
  id: number; parcel_id: string; status: string; label: string; tracking: string | null;
  dispatched: string; days: number; picked: boolean;
  order: { id: number; num: string; name: string; status: string } | null;
}

const fmt = (iso: string) => new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
const ADMIN_ES: Record<string, string> = { nueva: 'Nueva', cortado: 'Cortado', falta_insumo: 'Falta insumo', empaquetado: 'Empaquetado', despachado: 'Despachado', entregado: 'Entregado', cancelada: 'Cancelada' };

function CopyBtn({ text, label = 'Copiar' }: { text: string; label?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" title={`Copiar ${text.length > 40 ? 'los IDs' : text}`}
      onClick={() => navigator.clipboard?.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1400); })}
      className={'inline-flex items-center gap-1 rounded-[var(--radius-control)] border border-line px-1.5 py-0.5 text-[length:var(--text-xs)] font-medium hover:bg-muted ' + (ok ? 'text-[var(--success)]' : 'text-ink')}>
      {ok ? <Check size={12} /> : <Copy size={12} />} {ok ? 'Copiado' : label}
    </button>
  );
}

export function CabifyLate({ userEmail: emailProp }: { userEmail?: string }) {
  const [userEmail, setUserEmail] = useState<string | undefined>(emailProp);
  useEffect(() => { if (!emailProp) supabase.auth.getUser().then(({ data }) => setUserEmail(data.user?.email || undefined)); }, [emailProp]);
  const [rows, setRows] = useState<Late[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [peek, setPeek] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const since = new Date(Date.now() - 30 * 86400000).toISOString();
    const { data } = await supabase.from('cabify_deliveries')
      .select('id, order_id, parcel_id, external_id, status, created_at, raw')
      .not('status', 'in', `(${['delivered', 'created', 'ready', ...CABIFY_DEAD].join(',')})`)
      .gt('created_at', since).not('parcel_id', 'like', 'demo%');
    const now = Date.now();
    const list = (data || []).map((d: any) => {
      const ev: any[] = Array.isArray(d.raw?.events) ? d.raw.events : [];
      const first = ev.filter(e => ['readytopickup', 'shipped'].includes(e.state)).map(e => Date.parse(e.at)).sort((a, b) => a - b)[0];
      const disp = first || Date.parse(d.created_at);
      return { d, disp, picked: PICKED.includes(d.status) || ev.some(e => PICKED.includes(e.state)) };
    }).filter(x => now - x.disp > LATE_DAYS * 86400000);
    const ids = Array.from(new Set(list.map(x => x.d.order_id).filter(Boolean)));
    const info = new Map<number, any>();
    if (ids.length) {
      const { data: os } = await supabase.from('orders').select('id, source, tiendanube_id, contact_name, status').in('id', ids);
      (os || []).forEach((o: any) => info.set(o.id, o));
    }
    setRows(list.map(({ d, disp, picked }) => {
      const o = d.order_id ? info.get(d.order_id) : null;
      return {
        id: d.id, parcel_id: d.parcel_id, status: d.status, picked,
        label: (String(d.external_id || '').match(/\d+\/\d+/) || [''])[0],
        tracking: d.raw?.shipped?.parcels?.[0]?.tracking_url || d.raw?.tracking_url || null,
        dispatched: new Date(disp).toISOString(), days: Math.floor((now - disp) / 86400000),
        order: o ? { id: o.id, num: orderDisplayId(o), name: o.contact_name || '—', status: o.status } : null,
      };
    }).sort((a, b) => b.days - a.days));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (r: Late) => {
    const who = r.order ? `${r.order.num} (${r.order.name})` : r.parcel_id;
    if (!window.confirm(`¿Cancelar en Cabify la etiqueta de ${who}?\n\nUsalo si el pedido salió por otro medio o ya no va por Cabify: así no la cobran. No se puede deshacer (habría que generar otra).`)) return;
    setBusy(r.parcel_id); setMsg(null);
    const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'cancel', parcel_id: r.parcel_id, created_by: userEmail } });
    setBusy(null);
    if (error || !data?.ok) { setMsg(`No se pudo cancelar: ${data?.error || error?.message || 'error'}`); return; }
    logActivity(userEmail, 'CABIFY_ETIQUETA_CANCELADA', { order_id: r.order?.id, note: `Etiqueta Cabify ${r.parcel_id} cancelada desde Demorados (${r.days} días sin retirar)` });
    setMsg(`Etiqueta de ${who} cancelada en Cabify.`);
    load();
  };

  const picked = rows.filter(r => r.picked);
  const notPicked = rows.filter(r => !r.picked);
  if (loading) return <div className="tw mb-6 h-24 animate-pulse rounded-[var(--radius-surface)] bg-muted" />;
  if (!rows.length) return null; // sin demorados no ocupa lugar

  const table = (list: Late[], kind: 'reclamar' | 'local') => (
    <TableCard>
      <Table>
        <THead><tr>
          <Th>Pedido</Th><Th>Cliente</Th><Th>Estado en Cabify</Th><Th>Despachado</Th><Th>ID Cabify</Th><Th>Seguimiento</Th>
        </tr></THead>
        <tbody>
          {list.map(r => (
            <Tr key={r.parcel_id}>
              <Td className="font-semibold">
                {r.order ? <button type="button" className="underline decoration-dotted underline-offset-2 hover:text-[var(--accent)]" onClick={() => setPeek(r.order!.id)}>{r.order.num}</button> : <span className="text-soft">envío manual</span>}
                {r.label && <span className="ml-1 text-soft">{r.label}</span>}
                {r.order && <div className="mt-0.5"><Badge size="sm" tone={r.order.status === 'entregado' ? 'warning' : 'neutral'} title={r.order.status === 'entregado' ? 'En el admin figura entregado, pero Cabify no lo entregó' : undefined}>{ADMIN_ES[r.order.status] || r.order.status}</Badge></div>}
              </Td>
              <Td className="whitespace-nowrap">{r.order?.name || '—'}</Td>
              <Td><Badge size="sm" tone={kind === 'reclamar' ? 'danger' : 'warning'} title={CABIFY_STATE_ES[r.status] || r.status}>{kind === 'local' ? 'Sin retirar' : (CABIFY_STATE_ES[r.status] || r.status)}</Badge></Td>
              <Td className="whitespace-nowrap tabular-nums">{fmt(r.dispatched)} · <b>{r.days} días</b></Td>
              <Td><span className="inline-flex items-center gap-1.5 whitespace-nowrap"><code className="select-all font-mono text-[length:var(--text-xs)] text-soft">{r.parcel_id}</code><CopyBtn text={r.parcel_id} /></span></Td>
              <Td>
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  {r.tracking ? <a href={r.tracking} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-[var(--accent)] hover:underline">Ver <ExternalLink size={13} /></a> : <span className="text-faint">—</span>}
                  {kind === 'local' && (
                    <Button variant="secondary" size="sm" loading={busy === r.parcel_id} disabled={!!busy} onClick={() => cancel(r)} title="Si salió por otro medio: cancelar la etiqueta para que no la cobren">Cancelar</Button>
                  )}
                </span>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableCard>
  );

  return (
    <section className="tw mb-8 w-full min-w-0">
      <div className="mb-3">
        <h3 className="flex items-center gap-2 text-[length:var(--text-lg)] font-semibold text-ink"><AlertTriangle size={18} className="text-[var(--danger)]" /> Demorados · {rows.length}</h3>
        <p className="text-[length:var(--text-sm)] text-soft">Despachados hace más de {LATE_DAYS} días y todavía sin entregar según Cabify.</p>
      </div>
      {msg && <p className="mb-3 text-[length:var(--text-sm)] text-ink">{msg}</p>}

      {picked.length > 0 && (
        <div className="mb-5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 font-semibold text-ink"><Truck size={16} /> Cabify lo tiene y no lo entregó · {picked.length}</div>
              <div className="text-[length:var(--text-sm)] text-soft">Ya lo retiró del local. <b>Reclamar a soporte de Cabify</b> con el ID.</div>
            </div>
            <CopyBtn text={picked.map(r => `${r.order ? r.order.num + ' ' + r.order.name : 'envío manual'}: ${r.parcel_id}`).join('\n')} label="Copiar todo para soporte" />
          </div>
          {table(picked, 'reclamar')}
        </div>
      )}
      {notPicked.length > 0 && (
        <div>
          <div className="mb-2">
            <div className="flex items-center gap-2 font-semibold text-ink"><PackageX size={16} /> Cabify nunca lo retiró del local · {notPicked.length}</div>
            <div className="text-[length:var(--text-sm)] text-soft">Si sigue en el local, reclamar el retiro. Si salió por otro medio, <b>cancelar la etiqueta</b> para que no la cobren.</div>
          </div>
          {table(notPicked, 'local')}
        </div>
      )}
      <OrderPeek orderId={peek} onClose={() => setPeek(null)} />
    </section>
  );
}

export default CabifyLate;
